import { useCallback, useEffect, useRef, useState } from 'react';

export type CountdownState = 'idle' | 'countdown';

export interface UseCountdownOptions {
  countFrom?: number;
  onComplete: () => void;
  onTick?: (count: number) => void;
}

export interface UseCountdownReturn {
  state: CountdownState;
  count: number | null;
  start: () => void;
  skip: () => void;
}

export function useCountdown({
  countFrom = 3,
  onComplete,
  onTick,
}: UseCountdownOptions): UseCountdownReturn {
  const [state, setState] = useState<CountdownState>('idle');
  const [count, setCount] = useState<number | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const onCompleteRef = useRef(onComplete);
  const onTickRef = useRef(onTick);

  onCompleteRef.current = onComplete;
  onTickRef.current = onTick;

  const clearTimer = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const start = useCallback(() => {
    clearTimer();

    setState('countdown');
    setCount(countFrom);
    onTickRef.current?.(countFrom);

    let current = countFrom;
    intervalRef.current = setInterval(() => {
      current -= 1;
      if (current <= 0) {
        clearTimer();
        setState('idle');
        setCount(null);
        onCompleteRef.current();
      } else {
        setCount(current);
        onTickRef.current?.(current);
      }
    }, 1000);
  }, [countFrom, clearTimer]);

  const skip = useCallback(() => {
    if (intervalRef.current === null) return;
    clearTimer();
    setState('idle');
    setCount(null);
    onCompleteRef.current();
  }, [clearTimer]);

  useEffect(() => {
    return () => clearTimer();
  }, [clearTimer]);

  return { state, count, start, skip };
}
